<template>
  <div v-if="show" class="modal-overlay" @click="$emit('close')">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h2>
          <i class="fas fa-upload"></i>
          Upload Documents
        </h2>
        <button @click="$emit('close')" class="modal-close">&times;</button>
      </div>
      
      <div class="modal-body">
        <!-- Upload Zone -->
        <div 
          class="upload-zone"
          :class="{ 'drag-over': dragOver }"
          @drop.prevent="handleDrop"
          @dragover.prevent="dragOver = true"
          @dragleave="dragOver = false"
          @click="$refs.fileInput.click()">
          <i class="fas fa-cloud-upload-alt"></i>
          <h3>Drag & drop files here</h3>
          <p>or click to browse</p>
          <input 
            type="file" 
            ref="fileInput"
            @change="handleFileSelect"
            multiple
            :accept="uploadAccept"
            style="display: none">
          <p class="file-types">Supported: {{ uploadAcceptLabel }}</p>
        </div>
        
        <!-- Selected Files -->
        <div v-if="selectedFiles.length > 0" class="selected-files">
          <h4>Selected Files ({{ selectedFiles.length }})</h4>
          <div v-for="(file, index) in selectedFiles" :key="index" class="file-item">
            <div class="file-info">
              <i class="fas fa-file-pdf"></i>
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">{{ formatFileSize(file.size) }}</span>
            </div>
            <button @click="removeFile(index)" class="remove-btn">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        
        <!-- Optional: Link to Specific Entity -->
        <div class="optional-section">
          <label class="checkbox-label">
            <input type="checkbox" v-model="linkToSpecificEntity">
            <span>Link to specific {{ entityTerm.singular }}</span>
          </label>
          
          <select 
            v-if="linkToSpecificEntity" 
            v-model="selectedEntityId"
            class="form-select">
            <option value="">Select {{ entityTerm.singular }}...</option>
            <option v-for="entity in entities" :key="entity.id" :value="entity.id">
              {{ entity.subject_entity_name }}
            </option>
          </select>
          
          <p class="help-text">
            <i class="fas fa-info-circle"></i>
            Leave unchecked and AI will automatically detect which {{ entityTerm.plural.toLowerCase() }}
            are mentioned in the documents
          </p>
        </div>
        
        <!-- Skip Processing Option -->
        <div class="skip-processing-section">
          <label class="checkbox-label skip-checkbox">
            <input type="checkbox" v-model="skipProcessing">
            <span><i class="fas fa-forward"></i> Upload for reference only</span>
          </label>
          
          <div v-if="skipProcessing" class="skip-info">
            <i class="fas fa-info-circle"></i>
            <span>
              Documents will be saved but <strong>not analyzed</strong>. Use this for:
            </span>
            <ul>
              <li>Sample documents for training</li>
              <li>Reference materials</li>
              <li>Documents to analyze later</li>
            </ul>
          </div>
        </div>
        
        <!-- Upload Progress -->
        <div v-if="uploading" class="upload-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <p>Uploading {{ currentFileIndex + 1 }} of {{ selectedFiles.length }}...</p>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="$emit('close')" class="btn btn-secondary">
          Cancel
        </button>
        <button 
          @click="uploadFiles" 
          :disabled="selectedFiles.length === 0 || uploading"
          :class="['btn', skipProcessing ? 'btn-secondary' : 'btn-primary']">
          <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
          <i v-else-if="skipProcessing" class="fas fa-cloud-upload-alt"></i>
          <i v-else class="fas fa-magic"></i>
          {{ buttonLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import { UI_CONSTANTS } from '@/constants';

export default {
  name: 'SmartUploadModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    entities: {
      type: Array,
      default: () => []
    },
    entityTerm: {
      type: Object,
      default: () => ({ singular: 'Company', plural: 'Companies' })
    },
    /** Compliance subject id to pre-select when opened from an entity row */
    initialSubjectId: {
      type: [String, Number],
      default: null
    },
    /** Files dropped/picked on the project-home zone before the modal opens */
    initialFiles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedFiles: [],
      dragOver: false,
      linkToSpecificEntity: false,
      selectedEntityId: '',
      uploading: false,
      uploadProgress: 0,
      currentFileIndex: 0,
      skipProcessing: false,
      uploadAccept: UI_CONSTANTS.COI_UPLOAD_ACCEPT,
      uploadAcceptLabel: UI_CONSTANTS.COI_UPLOAD_ACCEPT_LABEL,
    };
  },
  computed: {
    buttonLabel() {
      if (this.uploading) {
        return 'Uploading...';
      }
      const fileCount = this.selectedFiles.length;
      const fileWord = fileCount === 1 ? 'File' : 'Files';
      if (this.skipProcessing) {
        return `Upload ${fileCount} ${fileWord} (No Analysis)`;
      }
      return `Upload ${fileCount} ${fileWord} & Extract`;
    }
  },
  watch: {
    show(isOpen) {
      if (isOpen) {
        this.applyInitialSubject();
        this.applyInitialFiles();
      } else {
        this.resetFormState();
      }
    },
    initialSubjectId() {
      if (this.show) {
        this.applyInitialSubject();
      }
    },
    initialFiles: {
      handler() {
        if (this.show) {
          this.applyInitialFiles();
        }
      },
      deep: true,
    }
  },
  methods: {
    applyInitialSubject() {
      if (this.initialSubjectId) {
        this.linkToSpecificEntity = true;
        this.selectedEntityId = String(this.initialSubjectId);
      }
    },
    applyInitialFiles() {
      if (this.initialFiles?.length) {
        this.selectedFiles = [];
        this.addFiles(this.initialFiles);
      }
    },
    resetFormState() {
      this.selectedFiles = [];
      this.dragOver = false;
      this.linkToSpecificEntity = false;
      this.selectedEntityId = '';
      this.uploading = false;
      this.uploadProgress = 0;
      this.currentFileIndex = 0;
      this.skipProcessing = false;
    },
    handleDrop(e) {
      this.dragOver = false;
      const files = Array.from(e.dataTransfer.files);
      this.addFiles(files);
    },
    
    handleFileSelect(e) {
      const files = Array.from(e.target.files);
      this.addFiles(files);
    },
    
    addFiles(files) {
      const supported = files.filter(f => {
        if (UI_CONSTANTS.COI_UPLOAD_MIME_TYPES.includes(f.type)) return true;
        // Some OS drag-drops omit MIME — fall back to extension
        const name = (f.name || '').toLowerCase();
        return name.endsWith('.pdf') || name.endsWith('.doc') || name.endsWith('.docx');
      });
      this.selectedFiles.push(...supported);
    },
    
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
    },
    
    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    },
    
    async uploadFiles() {
      this.uploading = true;
      this.uploadProgress = 0;
      
      const uploadedDocuments = [];
      
      for (let i = 0; i < this.selectedFiles.length; i++) {
        this.currentFileIndex = i;
        const file = this.selectedFiles[i];
        
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          // If skip processing, use the simpler upload endpoint
          if (this.skipProcessing) {
            formData.append('skip_processing', 'true');
            
            const response = await api.post(
              `/projects/${this.projectId}/documents/upload/`,
              formData,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                  this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                }
              }
            );
            
            uploadedDocuments.push({
              id: response.data.document?.id,
              name: file.name,
              skipProcessing: true
            });
            
          } else {
            // Normal flow: upload and extract entities
            formData.append('project_id', this.projectId);
            
            if (this.linkToSpecificEntity && this.selectedEntityId) {
              formData.append('subject_id', this.selectedEntityId);
            }
            
            // Upload and extract
            const response = await api.post(
              `/projects/${this.projectId}/upload-and-extract/`,
              formData,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                  this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                }
              }
            );
            
            // Emit event to show entity extraction modal
            this.$emit('show-entity-extraction', {
              documentId: response.data.document_id,
              documentName: file.name,
              documentUrl: response.data.document_url,
              extractedEntities: response.data.extracted_entities || [],
              matchStrategy: response.data.match_strategy || 'entity_name',
              subjectId: response.data.subject_id || null,
              file: file
            });
          }
          
          // Clear for next file (if multiple)
          if (i < this.selectedFiles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error('Error uploading file:', error);
          alert(`Failed to upload ${file.name}. Please try again.`);
        }
      }
      
      // If skip processing, emit upload-complete event
      if (this.skipProcessing && uploadedDocuments.length > 0) {
        this.$emit('upload-complete', {
          documents: uploadedDocuments,
          skipProcessing: true,
          message: `${uploadedDocuments.length} document(s) uploaded for reference (no analysis)`
        });
      }
      
      this.uploading = false;
      this.resetFormState();
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1500;
  padding: 2rem;
}

.modal-container {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s ease;
}

.modal-close:hover {
  background: #f1f5f9;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

.upload-zone {
  border: 3px dashed #cbd5e0;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.upload-zone:hover,
.upload-zone.drag-over {
  border-color: #667eea;
  background: #eff6ff;
}

.upload-zone i {
  font-size: 4rem;
  color: #667eea;
  margin-bottom: 1rem;
}

.upload-zone h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.25rem;
  color: #1a202c;
}

.upload-zone p {
  margin: 0;
  color: #64748b;
}

.file-types {
  font-size: 0.875rem;
  color: #94a3b8;
  margin-top: 1rem;
}

.selected-files {
  margin-top: 1.5rem;
}

.selected-files h4 {
  font-size: 1rem;
  color: #1a202c;
  margin-bottom: 1rem;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border-radius: 6px;
  margin-bottom: 0.5rem;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.file-info i {
  color: #ef4444;
  font-size: 1.25rem;
}

.file-name {
  font-weight: 500;
  color: #1a202c;
}

.file-size {
  font-size: 0.875rem;
  color: #94a3b8;
}

.remove-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.optional-section {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: #eff6ff;
  border-radius: 8px;
  border: 1px solid #bfdbfe;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: #1a202c;
  margin-bottom: 1rem;
  cursor: pointer;
}

.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.9375rem;
  margin-bottom: 1rem;
}

.help-text {
  font-size: 0.875rem;
  color: #3b82f6;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
}

.help-text i {
  margin-top: 0.125rem;
}

.upload-progress {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
}

.progress-bar {
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.75rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea, #764ba2);
  transition: width 0.3s ease;
}

.upload-progress p {
  margin: 0;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.9375rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: none;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a67d8;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: white;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #f8fafc;
}

/* Skip Processing Section */
.skip-processing-section {
  margin-top: 1.5rem;
  padding: 1.25rem;
  background: #fef3c7;
  border-radius: 8px;
  border: 1px solid #fcd34d;
}

.skip-checkbox {
  color: #92400e;
}

.skip-checkbox span {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.skip-checkbox i {
  color: #d97706;
}

.skip-info {
  margin-top: 1rem;
  padding: 1rem;
  background: #fffbeb;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #78350f;
}

.skip-info > i {
  color: #d97706;
  margin-right: 0.5rem;
}

.skip-info ul {
  margin: 0.75rem 0 0 1.5rem;
  padding: 0;
}

.skip-info li {
  margin: 0.25rem 0;
  color: #92400e;
}
</style>

